export const SOLANA_DEVNET_ORCA_POOL =
  "5GPCnpWwoDayCP5FVCJT1cS7dePufC5FrjPTVvPoUYnm";
export const SOLANA_DEVNET_SWAP_USDC =
  "4zMMC9srt5Ri5X14GAgXhaHii3GnPAEERYPJgZJDncDU";
export interface SolanaSwapRequest {
  network: string;
  programId: string;
  kind: "swap";
  mint: string | null;
  outputMint: string | null;
  treeIndex: number;
  inputCount: number;
  outputCount: number;
  amount: string;
  slippageBps: number;
}
export interface SolanaSwapQuote {
  version: 1 | 2;
  request: SolanaSwapRequest;
  feeRecipient: string;
  fee: string;
  swapInputAmount: string;
  orcaTradingFee: string;
  grossOutputAmount: string;
  protocolFee: string;
  partnerFee: string;
  partnerFeeBps: number;
  feePolicy: "onchain-protocol-only" | "private-input-fees";
  feePolicyHash?: `0x${string}`;
  feeRecipients?: {
    protocol: SolanaSwapFeeRecipient | null;
    partner: SolanaSwapFeeRecipient | null;
  };
  feeBreakdown: {
    fee: string;
    networkFee: string;
    accountRent: string;
    relayerMargin: string;
    networkFeeLamports: string;
    accountRentLamports: string;
    refundableRentLamports: string;
    conversion: "native-sol" | "orca-exact-output";
    conversionSlippageBps: number;
  };
  outputAmount: string;
  minOutputAmount: string;
  whirlpool: string;
  protocolFeeBps: number;
  deadlineSlot: string;
  issuedAt: number;
  expiresAt: number;
}
export interface SolanaSwapFeeRecipient {
  handle: string;
  mpk: `0x${string}`;
  inboxPublicKey: `0x${string}`;
  recipientKey: `0x${string}`;
  recordHash: `0x${string}`;
  version: number;
}
export interface SolanaSwapApproval {
  quote: SolanaSwapQuote;
  selectedCommitments: string[];
  inputTotal: bigint;
}
export function validateSolanaSwapQuote(
  value: unknown,
  request: SolanaSwapRequest,
  now = Date.now(),
): SolanaSwapQuote {
  const q = value as SolanaSwapQuote;
  const uint = (v: unknown): v is string =>
    typeof v === "string" &&
    /^(0|[1-9][0-9]{0,19})$/.test(v) &&
    BigInt(v) < 1n << 64n;
  if (
    !q ||
    ![1, 2].includes(q.version) ||
    !q.request ||
    Object.keys(request).some(
      (k) =>
        q.request[k as keyof SolanaSwapRequest] !==
        request[k as keyof SolanaSwapRequest],
    ) ||
    request.network !== "solana-devnet" ||
    q.whirlpool !== SOLANA_DEVNET_ORCA_POOL ||
    typeof q.feeRecipient !== "string" ||
    ![
      q.fee,
      q.outputAmount,
      q.minOutputAmount,
      q.deadlineSlot,
      request.amount,
    ].every(uint) ||
    BigInt(q.fee) >= BigInt(request.amount) ||
    BigInt(q.minOutputAmount) <= 0n ||
    BigInt(q.minOutputAmount) > BigInt(q.outputAmount) ||
    BigInt(q.deadlineSlot) === 0n ||
    !Number.isInteger(q.protocolFeeBps) ||
    q.protocolFeeBps < 0 ||
    q.protocolFeeBps > (q.version === 2 ? 9999 : 100) ||
    !Number.isSafeInteger(q.issuedAt) ||
    !Number.isSafeInteger(q.expiresAt) ||
    q.issuedAt > now + 5000 ||
    q.expiresAt <= now ||
    q.expiresAt <= q.issuedAt ||
    q.expiresAt - q.issuedAt > 60_000
  )
    throw new Error(
      "Invalid or expired Orca quote. Get a new quote before confirming.",
    );
  if (
    !(
      (request.mint === null &&
        request.outputMint === SOLANA_DEVNET_SWAP_USDC) ||
      (request.mint === SOLANA_DEVNET_SWAP_USDC && request.outputMint === null)
    )
  )
    throw new Error("Unsupported Orca devnet pair");
  const costs = q.feeBreakdown;
  const privateFees = q.version === 2;
  const validRecipient = (r: SolanaSwapFeeRecipient | null | undefined) =>
    !!r &&
    /^[a-z0-9][a-z0-9_-]*\.stealth$/.test(r.handle) &&
    [r.mpk, r.inboxPublicKey, r.recipientKey, r.recordHash].every(
      (v) =>
        typeof v === "string" &&
        /^0x[0-9a-f]{64}$/i.test(v) &&
        BigInt(v) !== 0n,
    ) &&
    Number.isSafeInteger(r.version) &&
    r.version > 0;
  if (
    privateFees &&
    (q.feePolicy !== "private-input-fees" ||
      !/^0x[0-9a-f]{64}$/i.test(q.feePolicyHash ?? "") ||
      BigInt(q.feePolicyHash!) === 0n ||
      !Number.isInteger(q.partnerFeeBps) ||
      q.partnerFeeBps < 0 ||
      q.protocolFeeBps + q.partnerFeeBps >= 10000 ||
      !q.feeRecipients ||
      (q.protocolFeeBps > 0
        ? !validRecipient(q.feeRecipients.protocol)
        : q.feeRecipients.protocol !== null) ||
      (q.partnerFeeBps > 0
        ? !validRecipient(q.feeRecipients.partner)
        : q.feeRecipients.partner !== null))
  )
    throw new Error(
      "Invalid private swap fee recipients or policy. Refresh the quote.",
    );
  if (
    !costs ||
    ![
      q.swapInputAmount,
      q.orcaTradingFee,
      q.grossOutputAmount,
      q.protocolFee,
      q.partnerFee,
      costs.fee,
      costs.networkFee,
      costs.accountRent,
      costs.relayerMargin,
      costs.networkFeeLamports,
      costs.accountRentLamports,
      costs.refundableRentLamports,
    ].every(uint) ||
    costs.fee !== q.fee ||
    BigInt(q.fee) !==
      BigInt(costs.networkFee) +
        BigInt(costs.accountRent) +
        BigInt(costs.relayerMargin) ||
    BigInt(q.swapInputAmount) +
      BigInt(q.fee) +
      (privateFees ? BigInt(q.protocolFee) + BigInt(q.partnerFee) : 0n) !==
      BigInt(request.amount) ||
    BigInt(q.orcaTradingFee) > BigInt(q.swapInputAmount) ||
    BigInt(q.grossOutputAmount) !==
      BigInt(q.outputAmount) +
        (privateFees ? 0n : BigInt(q.protocolFee) + BigInt(q.partnerFee)) ||
    BigInt(q.protocolFee) !==
      (BigInt(privateFees ? request.amount : q.grossOutputAmount) *
        BigInt(q.protocolFeeBps)) /
        10_000n ||
    (privateFees
      ? BigInt(q.partnerFee) !==
        (BigInt(request.amount) * BigInt(q.partnerFeeBps)) / 10_000n
      : q.partnerFee !== "0" ||
        q.partnerFeeBps !== 0 ||
        q.feePolicy !== "onchain-protocol-only") ||
    BigInt(costs.networkFeeLamports) <= 0n ||
    costs.conversion !==
      (request.mint === null ? "native-sol" : "orca-exact-output") ||
    costs.conversionSlippageBps !== (request.mint === null ? 0 : 50) ||
    (request.mint === null &&
      (costs.networkFee !== costs.networkFeeLamports ||
        costs.accountRent !== costs.accountRentLamports))
  )
    throw new Error("Invalid swap fee breakdown. Refresh the quote.");
  return q;
}
